import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { withErrorRef } from '../../../../core/error-ref';
import { IconComponent } from '../../../../shared/icon/icon';
import { DocAssessStatus } from '../../../../core/status/doc-assess.types';
import { DocVerdictComponent } from '../doc-verdict/doc-verdict';

/**
 * Client-side mirror of the backend's upload limits
 * (`flow_ai/files/services.py`: `MAX_UPLOAD_BYTES`, `ALLOWED_CONTENT_TYPES`) —
 * catches an obvious mistake before the round trip. The backend remains the
 * real authority (content-type here is the browser-reported MIME type, not a
 * magic-byte sniff, so it's a UX nicety, not a security control).
 */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024; // 100 MB

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const ALLOWED_UPLOAD_CONTENT_TYPES = new Set<string>([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  XLSX_CONTENT_TYPE,
]);

/**
 * Extensions accepted even when the browser reports no usable MIME type —
 * Windows and some scanners hand a PDF over as `application/octet-stream` or
 * `application/x-pdf`, and an upper-case `.PDF` is common (feedback j2NXBFQ3).
 * The backend sniffs the bytes, so letting these through costs nothing.
 */
const ALLOWED_UPLOAD_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'xlsx']);

/** The file picker's filter — the same types as the two lists above. */
export const UPLOAD_ACCEPT = [
  ...ALLOWED_UPLOAD_CONTENT_TYPES,
  ...[...ALLOWED_UPLOAD_EXTENSIONS].map((e) => `.${e}`),
].join(',');

/** The limits, in the words shown beside every drop zone. */
export const UPLOAD_LIMITS_TEXT = 'PDF, image (JPG, PNG) or Excel · up to 100 MB each';

/**
 * The message for a failed upload, specific wherever the failure tells us why.
 *
 * A 413 is answered by the proxy, not Django, so it has no `detail` to show —
 * without this it read "Something went wrong", which is what the Ops tester saw
 * when a 1.2 MB signed agreement hit the proxy's old 1 MB cap (feedback j2NXBFQ3).
 */
export function uploadErrorMessage(err: unknown): string {
  const response = err as HttpErrorResponse;
  if (response?.status === 413) {
    return 'This file is larger than the 100 MB upload limit. Please upload a smaller file.';
  }
  if (response?.status === 0) {
    return 'The upload was interrupted. Check your connection and try again.';
  }
  const detail = (response?.error as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string') return detail;
  return withErrorRef('The upload failed. Please try again.', response);
}

/** A document bucket the applicant can drop files into. */
export interface DocDropType<T extends string> {
  value: T;
  label: string;
  hint?: string;
}

/** The minimal shape of a stored document this block needs to render a verdict. */
export interface AssessedDoc {
  short_id: string;
  file_name: string;
  assess_status: DocAssessStatus;
  assess_message: string;
}

/** One file the applicant added: its upload lifecycle + (once done) the server doc. */
export interface DocUpload<T extends string> {
  /** Client-side id, stable for the life of the upload. */
  id: string;
  docType: T;
  fileName: string;
  state: 'uploading' | 'done' | 'error';
  /** Populated once the upload completes — carries the AI verdict. */
  doc: AssessedDoc | null;
  /** Set when state === 'error'. */
  error?: string;
}

/**
 * A single document drop block: drag-and-drop or browse to add one or more
 * files, each uploaded immediately and shown with a traffic-light verdict
 * (green = looks good, amber = evaluating / worth a look, red = problem).
 *
 * Stateless about uploading itself — the parent owns the HTTP call and patches
 * each {@link DocUpload}; this block only captures files and renders status.
 */
@Component({
  selector: 'app-doc-drop',
  imports: [MatTooltipModule, IconComponent, DocVerdictComponent],
  templateUrl: './doc-drop.html',
  styleUrl: './doc-drop.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocDropComponent<T extends string> {
  protected readonly accept = UPLOAD_ACCEPT;
  /** The bucket this block represents (e.g. Bank statements). */
  readonly docType = input.required<DocDropType<T>>();
  /** Uploads belonging to this bucket. */
  readonly uploads = input.required<DocUpload<T>[]>();
  /** Disable interaction (e.g. before a draft exists). */
  readonly disabled = input(false);
  /** Upload ids whose server-side delete is in flight (remove button disabled). */
  readonly deletingIds = input<ReadonlySet<string>>(new Set());

  /** Emitted when the applicant adds files (drag-drop or browse). */
  readonly filesPicked = output<File[]>();
  /** Emitted to remove an upload (server-side delete if it was stored). */
  readonly remove = output<string>();

  protected readonly limitsText = UPLOAD_LIMITS_TEXT;
  protected readonly isDragging = signal(false);
  /** Set when the most recent pick included a rejected file; null otherwise. */
  protected readonly rejectionError = signal<string | null>(null);

  protected onDragOver(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.isDragging.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
    if (this.disabled()) return;
    this.pickFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  protected onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pickFiles(Array.from(input.files ?? []));
    // Reset so picking the same file again re-fires change.
    input.value = '';
  }

  /** Splits picked files into accepted / rejected, emitting only the accepted ones. */
  private pickFiles(files: File[]): void {
    if (!files.length) return;

    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const file of files) {
      const reason = this.rejectionReason(file);
      if (reason) {
        rejected.push(`${file.name} (${reason})`);
      } else {
        accepted.push(file);
      }
    }

    this.rejectionError.set(rejected.length ? `Couldn't add ${rejected.join(', ')}.` : null);
    if (accepted.length) {
      this.filesPicked.emit(accepted);
    }
  }

  /** Null when the file is fine; otherwise a short human-readable rejection reason. */
  private rejectionReason(file: File): string | null {
    if (file.size > MAX_UPLOAD_BYTES) {
      return 'over 100 MB';
    }
    // The browser's MIME type is unreliable (empty, octet-stream, x-pdf), so a
    // known extension is enough — block only when neither looks acceptable.
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (
      file.type &&
      !ALLOWED_UPLOAD_CONTENT_TYPES.has(file.type) &&
      !ALLOWED_UPLOAD_EXTENSIONS.has(extension)
    ) {
      return 'unsupported file type';
    }
    return null;
  }

  /**
   * Error text to force the verdict row red, or null for the normal flow.
   * A still-uploading row has no doc yet → the verdict component shows the
   * PENDING ("Checking…") state from the passed status.
   */
  protected errorTextFor(u: DocUpload<T>): string | null {
    if (u.state === 'error') {
      return u.error ?? 'Upload failed. Please try again.';
    }
    return null;
  }

  protected isDeleting(u: DocUpload<T>): boolean {
    return this.deletingIds().has(u.id);
  }
}
